/**
 * Conversion between `document_config.routing` rules and the editable rows of the
 * Routing table. Each row keeps its original rule so keys the UI doesn't edit
 * (e.g. `response_status_members`) and explicit `null`s survive a save untouched:
 * a field is only rewritten when the user actually changed it.
 */
import { v4 as uuidv4 } from "uuid";
import { TextNotesRoutingRule } from "../../../../../data-model/deliveryStepsConfig";

export interface RoutingRuleRow {
  id: string;
  routeVia: string[];
  responseStatus: string[];
  alias: string;
  noteState: string;
  signRequired: boolean;
  canTransitionToSigned: boolean;
  headingTemplate: string;
  whatWeAdd: string[];
  /** Rule as loaded from Firestore; absent for rows added in this session. */
  original?: TextNotesRoutingRule;
}

type RoutingRuleValues = Omit<RoutingRuleRow, "id" | "original">;

const toList = (value: string[] | null | undefined): string[] =>
  Array.isArray(value) ? value : [];

const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * Reads a Firestore routing rule into editable row values.
 *
 * @param rule - Routing rule from `document_config.routing`
 * @returns Row values (no id / original)
 */
function ruleToValues(rule: TextNotesRoutingRule): RoutingRuleValues {
  return {
    routeVia: toList(rule.route_via),
    responseStatus: toList(rule.response_status),
    alias: rule.response_status_alias ?? "",
    noteState: rule.text_notes_status?.state ?? "",
    signRequired: rule.sign_required === true,
    canTransitionToSigned:
      rule.text_notes_status?.can_transition_to_signed === true,
    headingTemplate: rule.text_notes_heading ?? "",
    whatWeAdd: toList(rule.what_we_add),
  };
}

/**
 * Builds editable rows for the Routing table.
 *
 * @param rules - `document_config.routing`, if present
 * @returns One row per routing rule
 */
export function routingFromConfig(
  rules: TextNotesRoutingRule[] | undefined,
): RoutingRuleRow[] {
  return (rules ?? []).map((rule) => ({
    id: uuidv4(),
    ...ruleToValues(rule),
    original: rule,
  }));
}

/** A blank routing rule row for the Add New Rule action. */
export function createEmptyRoutingRow(): RoutingRuleRow {
  return {
    id: uuidv4(),
    routeVia: [],
    responseStatus: [],
    alias: "",
    noteState: "unsigned",
    signRequired: false,
    canTransitionToSigned: false,
    headingTemplate: "",
    whatWeAdd: [],
  };
}

/**
 * Writes a row back to a routing rule, changing only the fields the user edited.
 *
 * @param row - Routing table row
 * @returns Rule for `document_config.routing`
 */
function rowToRule(row: RoutingRuleRow): TextNotesRoutingRule {
  const base = row.original ?? {};
  const before = ruleToValues(base);
  const rule: TextNotesRoutingRule = { ...base };

  if (!row.original || !sameList(before.routeVia, row.routeVia)) {
    rule.route_via = row.routeVia;
  }
  if (!row.original || !sameList(before.responseStatus, row.responseStatus)) {
    rule.response_status = row.responseStatus;
  }
  if (!row.original || !sameList(before.whatWeAdd, row.whatWeAdd)) {
    rule.what_we_add = row.whatWeAdd;
  }
  if (!row.original || before.alias !== row.alias) {
    rule.response_status_alias = row.alias.trim() || null;
  }
  if (!row.original || before.headingTemplate !== row.headingTemplate) {
    rule.text_notes_heading = row.headingTemplate.trim() || null;
  }
  if (!row.original || before.signRequired !== row.signRequired) {
    rule.sign_required = row.signRequired;
  }
  if (
    !row.original ||
    before.noteState !== row.noteState ||
    before.canTransitionToSigned !== row.canTransitionToSigned
  ) {
    rule.text_notes_status = {
      ...base.text_notes_status,
      state: row.noteState,
      can_transition_to_signed: row.canTransitionToSigned,
    };
  }
  return rule;
}

/**
 * Serialises the Routing table back to `document_config.routing`.
 *
 * @param rows - Current routing rows
 * @returns Routing rules in row order
 */
export function routingToConfig(
  rows: RoutingRuleRow[],
): TextNotesRoutingRule[] {
  return rows.map(rowToRule);
}
